import React, { useState, useEffect } from 'react';
import { User } from '../types/auth';
import authService from '../services/auth-service';

interface AdminPanelProps {
  user: User | null;
}

interface LifeExpectancyData {
  death_table: any[][];
  source: string;
  standard_deviation: number;
  standard_deviation_source: string;
  female_premium: number;
  female_premium_source: string;
}

interface DashboardData {
  life_expectancy: {
    total_rows: number;
    scenarios_available: number;
    last_updated: string;
    sample_data: any[][];
  };
  excel_data: {
    file_loaded: boolean;
    sheets_available: string[];
    total_sheets: number;
  };
  system_status: {
    admin_user: string;
    server_running: boolean;
    database_connected: boolean;
  };
}

interface SavedScenario {
  id: number;
  calculator: string;
  user_email: string;
  user_tier: string;
  scenario_name: string;
  description: string;
  created_at: string;
  updated_at: string;
  is_public: boolean;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [lifeExpectancyData, setLifeExpectancyData] = useState<LifeExpectancyData | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [scenarios, setScenarios] = useState<SavedScenario[]>([]);
  const [testResult, setTestResult] = useState<any>(null);
  const [testParams, setTestParams] = useState({
    age: 60,
    gender: 'Male',
    scenario: 2
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  const [parameters, setParameters] = useState({
    standard_deviation: 15.0,
    standard_deviation_source: 'Statistics Canada Life Tables',
    female_premium: 2.5,
    female_premium_source: 'Statistics Canada Life Tables'
  });

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const fetchLifeExpectancyData = async () => {
    setLoading(true);
    try {
      const response = await fetch('/backend/admin/life-expectancy-data', {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setLifeExpectancyData(data.data);
        // Update parameters from loaded data
        if (data.data.standard_deviation) {
          setParameters(prev => ({
            ...prev,
            standard_deviation: data.data.standard_deviation,
            standard_deviation_source: data.data.standard_deviation_source || prev.standard_deviation_source,
            female_premium: data.data.female_premium || prev.female_premium,
            female_premium_source: data.data.female_premium_source || prev.female_premium_source
          }));
        }
        showMessage('success', 'Life expectancy data loaded successfully');
      }
    } catch (error) {
      showMessage('error', 'Failed to load life expectancy data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await fetch('/backend/admin/admin-dashboard', {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setDashboardData(data.dashboard);
      }
    } catch (error) {
      showMessage('error', 'Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllScenarios = async () => {
    try {
      const response = await fetch('/backend/admin/scenarios', {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.scenarios) {
        setScenarios(data.scenarios);
      }
    } catch (error) {
      showMessage('error', 'Failed to load scenarios');
      console.error(error);
    }
  };

  const testLifeExpectancy = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/backend/admin/test-life-expectancy?age=${testParams.age}&gender=${testParams.gender}&scenario=${testParams.scenario}`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setTestResult(data);
        showMessage('success', 'Life expectancy test completed');
      }
    } catch (error) {
      showMessage('error', 'Failed to test life expectancy calculation');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const reloadExcelData = async () => {
    setLoading(true);
    try {
      const response = await fetch('/backend/admin/reload-excel-data', {
        method: 'POST',
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        showMessage('success', 'Excel data reloaded successfully');
        fetchDashboardData();
      }
    } catch (error) {
      showMessage('error', 'Failed to reload Excel data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateLifeExpectancyTable = async (tableData: any) => {
    setLoading(true);
    try {
      const response = await fetch('/backend/admin/update-life-expectancy-table', {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tableData)
      });
      const data = await response.json();
      if (data.success) {
        showMessage('success', `Life expectancy table updated successfully (${data.rows_updated} rows)`);
        fetchLifeExpectancyData();
      }
    } catch (error) {
      showMessage('error', 'Failed to update life expectancy table');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchData = async () => {
        try {
            await fetchDashboardData();
            await fetchLifeExpectancyData();
            await fetchAllScenarios();
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
    };

    fetchData();
}, []);

  if (!user || !user.is_admin) return null;

  const adminPanelStyle = {
    padding: '24px',
    background: '#f5f5f5',
    minHeight: '100vh'
  };

  const adminHeaderStyle = {
    marginBottom: '24px'
  };

  const adminSectionStyle = {
    background: 'white',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '24px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  const statusCardsStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px'
  };

  const statusCardStyle = (type: string) => ({
    padding: '12px',
    borderRadius: '4px',
    borderLeft: '4px solid',
    ...(type === 'info' && {
      background: '#e6f7ff',
      borderLeftColor: '#1890ff'
    }),
    ...(type === 'success' && {
      background: '#f6ffed',
      borderLeftColor: '#52c41a'
    }),
    ...(type === 'warning' && {
      background: '#fffbe6',
      borderLeftColor: '#faad14'
    }),
    ...(type === 'error' && {
      background: '#fff2f0',
      borderLeftColor: '#ff4d4f'
    })
  });

  const testControlsStyle = {
    display: 'flex',
    gap: '12px',
    alignItems: 'center' as const,
    marginBottom: '16px',
    flexWrap: 'wrap' as const
  };

  const adminInputStyle = {
    padding: '8px 12px',
    border: '1px solid #d9d9d9',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100px'
  };

  const adminSelectStyle = {
    padding: '8px 12px',
    border: '1px solid #d9d9d9',
    borderRadius: '4px',
    fontSize: '14px',
    width: '150px'
  };

  const adminButtonStyle = {
    padding: '8px 16px',
    border: '1px solid #d9d9d9',
    borderRadius: '4px',
    background: 'white',
    cursor: 'pointer',
    fontSize: '14px'
  };

  const adminButtonPrimaryStyle = {
    ...adminButtonStyle,
    background: '#1890ff',
    color: 'white',
    borderColor: '#1890ff'
  };

  const testResultStyle = (type: string) => ({
    padding: '12px',
    borderRadius: '4px',
    marginTop: '16px',
    ...(type === 'success' && {
      background: '#f6ffed',
      border: '1px solid #b7eb8f'
    })
  });

  const dataTableStyle = {
    overflowX: 'auto' as const,
    marginBottom: '16px'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '12px'
  };

  const thTdStyle = {
    padding: '8px',
    border: '1px solid #d9d9d9',
    textAlign: 'center' as const
  };

  const thStyle = {
    ...thTdStyle,
    background: '#fafafa',
    fontWeight: 'bold'
  };

  const messageStyle = (type: string) => ({
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '16px',
    ...(type === 'success' && {
      background: '#f6ffed',
      border: '1px solid #b7eb8f',
      color: '#52c41a'
    }),
    ...(type === 'error' && {
      background: '#fff2f0',
      border: '1px solid #ffccc7',
      color: '#ff4d4f'
    }),
    ...(type === 'info' && {
      background: '#e6f7ff',
      border: '1px solid #91d5ff',
      color: '#1890ff'
    })
  });

  const parameterInputStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginBottom: '16px'
  };

  const inputGroupStyle = {
    display: 'flex',
    gap: '12px',
    alignItems: 'center' as const
  };

  const labelStyle = {
    minWidth: '200px',
    fontWeight: 'bold' as const,
    fontSize: '14px'
  };

  const inputStyle = {
    padding: '8px 12px',
    border: '1px solid #d9d9d9',
    borderRadius: '4px',
    fontSize: '14px',
    width: '120px'
  };

  const sourceInputStyle = {
    ...inputStyle,
    width: '250px'
  };

  return (
    <div style={adminPanelStyle}>
      <div style={adminHeaderStyle}>
        <h2>Admin Panel</h2>
        <p>Welcome, {user.email}! Manage app data and test calculations.</p>
      </div>
      
      {message && (
        <div style={messageStyle(message.type)}>
          {message.text}
        </div>
      )}

      {/* Dashboard Overview */}
      <div style={adminSectionStyle}>
        <h3>System Overview</h3>
        <div style={statusCardsStyle}>
          {dashboardData && (
            <>
              <div style={statusCardStyle('info')}>
                <strong>Life Expectancy Data:</strong> {dashboardData.life_expectancy.total_rows} rows loaded
              </div>
              <div style={statusCardStyle(dashboardData.excel_data.file_loaded ? 'success' : 'warning')}>
                <strong>Excel File:</strong> {dashboardData.excel_data.file_loaded ? 'Loaded' : 'Not Loaded'} ({dashboardData.excel_data.total_sheets} sheets)
              </div>
              <div style={statusCardStyle(dashboardData.system_status.server_running ? 'success' : 'error')}>
                <strong>Server Status:</strong> {dashboardData.system_status.server_running ? 'Running' : 'Stopped'}
              </div>
            </>
          )}
          <button 
            style={adminButtonStyle}
            onClick={reloadExcelData}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Reload Excel Data'}
          </button>
        </div>
      </div>

      {/* Life Expectancy Parameters */}
      <div style={adminSectionStyle}>
        <h3>Life Expectancy Data Management</h3>
        
        {/* Current Data Summary */}
        {lifeExpectancyData && lifeExpectancyData.death_table && (
          <div style={{ marginBottom: '20px', padding: '12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#52c41a' }}>Current Data</h4>
            <p style={{ margin: '4px 0' }}><strong>Source:</strong> {lifeExpectancyData.source}</p>
            <p style={{ margin: '4px 0' }}><strong>Total Rows:</strong> {lifeExpectancyData.death_table.length}</p>
            <p style={{ margin: '4px 0' }}><strong>Standard Deviation:</strong> {parameters.standard_deviation}</p>
            <p style={{ margin: '4px 0' }}><strong>Female Premium:</strong> {parameters.female_premium}</p>
          </div>
        )}

        {/* CSV Upload/Download */}
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0' }}>Import/Export CSV</h4>
          
          {/* Download Current Data */}
          <div style={{ marginBottom: '16px' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Download current data as CSV:</p>
            <button 
              style={adminButtonStyle}
              onClick={() => {
                if (lifeExpectancyData && lifeExpectancyData.death_table) {
                  // Create CSV content in the simple format
                  const csvContent = [
                    'Age,Life Expectancy',
                    ...lifeExpectancyData.death_table.map(row => `${row[0]},${row[2]}`)
                  ].join('\n');
                  
                  const dataBlob = new Blob([csvContent], { type: 'text/csv' });
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'life_expectancy_data.csv';
                  link.click();
                  URL.revokeObjectURL(url);
                  showMessage('success', 'CSV file downloaded successfully');
                }
              }}
              disabled={!lifeExpectancyData}
            >
              Download CSV
            </button>
          </div>
          
          {/* Upload CSV */}
          <div>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Upload new data from CSV:</p>
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    try {
                      let csvContent = event.target?.result as string;
                      
                      // If it's an XLSX file, we need to convert it
                      if (file.name.endsWith('.xlsx')) {
                        // For now, we'll show an error since XLSX conversion requires additional libraries
                        // In a real implementation, you'd use a library like SheetJS
                        showMessage('error', 'XLSX conversion not implemented yet. Please convert to CSV first.');
                        return;
                      }
                      
                      const lines = csvContent.split('\n');
                      
                      // Parse CSV data in the simple format: Age, Life Expectancy
                      const deathTable = [];
                      let headerFound = false;
                      
                      for (const line of lines) {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) continue;
                        
                        // Check if this is the header row
                        if (trimmedLine.toLowerCase().includes('age') && trimmedLine.toLowerCase().includes('life expectancy')) {
                          headerFound = true;
                          continue;
                        }
                        
                        if (headerFound) {
                          const parts = trimmedLine.split(',').map(p => p.trim());
                          if (parts.length >= 2) {
                            const age = parseInt(parts[0]);
                            const lifeExpectancy = parseFloat(parts[1]);
                            
                            if (!isNaN(age) && !isNaN(lifeExpectancy)) {
                              // Format: [age, age_group, base_life_expectancy]
                              const ageGroup = age === 0 ? '0 years' : age === 1 ? '1 year' : `${age} years`;
                              deathTable.push([age, ageGroup, lifeExpectancy]);
                            }
                          }
                        }
                      }
                      
                      // Validate the data
                      if (deathTable.length === 0) {
                        throw new Error('No valid data rows found in CSV');
                      }
                      
                      // Create the data structure
                      const uploadData = {
                        source: 'Uploaded CSV',
                        death_table: deathTable,
                        standard_deviation: parameters.standard_deviation,
                        standard_deviation_source: parameters.standard_deviation_source,
                        female_premium: parameters.female_premium,
                        female_premium_source: parameters.female_premium_source
                      };
                      
                      // Upload to server
                      updateLifeExpectancyTable(uploadData);
                      
                    } catch (error) {
                      showMessage('error', `Failed to parse CSV: ${error instanceof Error ? error.message : 'Invalid format'}`);
                      console.error('CSV parsing error:', error);
                    }
                  };
                  reader.readAsText(file);
                }
              }}
              style={{ marginTop: '8px' }}
            />
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
              CSV format: Age, Life Expectancy<br/>
              Example: 0,81.70 or 1,81.07
            </p>
          </div>
        </div>

        {/* Parameter Editing */}
        <div style={{ marginTop: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0' }}>Edit Parameters</h4>
          <div style={parameterInputStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Standard Deviation:</label>
              <input
                type="number"
                step="0.1"
                value={parameters.standard_deviation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParameters({ ...parameters, standard_deviation: parseFloat(e.target.value) || 0 })}
                style={inputStyle}
              />
              <input
                type="text"
                placeholder="Source"
                value={parameters.standard_deviation_source}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParameters({ ...parameters, standard_deviation_source: e.target.value })}
                style={sourceInputStyle}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Female Life Expectancy Premium:</label>
              <input
                type="number"
                step="0.1"
                value={parameters.female_premium}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParameters({ ...parameters, female_premium: parseFloat(e.target.value) || 0 })}
                style={inputStyle}
              />
              <input
                type="text"
                placeholder="Source"
                value={parameters.female_premium_source}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParameters({ ...parameters, female_premium_source: e.target.value })}
                style={sourceInputStyle}
              />
            </div>
            <button 
              style={adminButtonPrimaryStyle}
              onClick={() => {
                if (lifeExpectancyData) {
                  const updatedData = {
                    ...lifeExpectancyData,
                    standard_deviation: parameters.standard_deviation,
                    standard_deviation_source: parameters.standard_deviation_source,
                    female_premium: parameters.female_premium,
                    female_premium_source: parameters.female_premium_source
                  };
                  updateLifeExpectancyTable(updatedData);
                }
              }}
              disabled={!lifeExpectancyData}
            >
              Update Parameters
            </button>
          </div>
        </div>
      </div>

      {/* Life Expectancy Testing */}
      <div style={adminSectionStyle}>
        <h3>Life Expectancy Testing</h3>
        <div style={testControlsStyle}>
          <input
            type="number"
            placeholder="Age"
            value={testParams.age}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTestParams({ ...testParams, age: parseInt(e.target.value) || 60 })}
            style={adminInputStyle}
          />
          <select
            value={testParams.gender}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTestParams({ ...testParams, gender: e.target.value })}
            style={adminSelectStyle}
          >
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
          <select
            value={testParams.scenario}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTestParams({ ...testParams, scenario: parseInt(e.target.value) })}
            style={adminSelectStyle}
          >
            <option value={1}>Conservative</option>
            <option value={2}>Moderate</option>
            <option value={3}>Aggressive</option>
            <option value={5}>Custom (51)</option>
            <option value={10}>Custom (56)</option>
          </select>
          <button 
            style={adminButtonPrimaryStyle}
            onClick={testLifeExpectancy}
            disabled={loading}
          >
            {loading ? 'Testing...' : 'Test Calculation'}
          </button>
        </div>
        
        {testResult && (
          <div style={testResultStyle('success')}>
            <strong>Result: {testResult.calculated_life_expectancy} years</strong>
            <p>Age: {testResult.age}, Gender: {testResult.gender}, Scenario: {testResult.scenario}</p>
          </div>
        )}
      </div>

      {/* Life Expectancy Data Management */}
      <div style={adminSectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0 }}>Life Expectancy Data Management</h3>
          <button 
            style={adminButtonStyle}
            onClick={fetchLifeExpectancyData}
            disabled={loading}
          >
            Refresh Data
          </button>
        </div>
        
        {lifeExpectancyData && lifeExpectancyData.death_table ? (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Death Table: {lifeExpectancyData.death_table.length} rows</strong>
              <p>Source: {lifeExpectancyData.source}</p>
            </div>
            
            <div style={dataTableStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Age</th>
                    <th style={thStyle}>Age Group</th>
                    <th style={thStyle}>Base Life Expectancy</th>
                  </tr>
                </thead>
                <tbody>
                  {lifeExpectancyData.death_table.slice(0, 10).map((row, index) => (
                    <tr key={index}>
                      <td style={thTdStyle}>{row[0]}</td>
                      <td style={thTdStyle}>{row[1]}</td>
                      <td style={thTdStyle}>{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <p style={{ color: '#666', fontSize: '12px', margin: 0 }}>
              Showing first 10 rows. Total: {lifeExpectancyData.death_table.length} rows
            </p>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading...</div>
        )}
      </div>

      {/* Data Export/Import */}
      <div style={adminSectionStyle}>
        <h3>Data Management</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <p>Export current life expectancy data for backup or editing:</p>
            <button 
              style={adminButtonStyle}
              onClick={() => {
                if (lifeExpectancyData && lifeExpectancyData.death_table) {
                  const exportData = {
                    ...lifeExpectancyData,
                    standard_deviation: parameters.standard_deviation,
                    standard_deviation_source: parameters.standard_deviation_source,
                    female_premium: parameters.female_premium,
                    female_premium_source: parameters.female_premium_source
                  };
                  const dataStr = JSON.stringify(exportData, null, 2);
                  const dataBlob = new Blob([dataStr], { type: 'application/json' });
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'life_expectancy_data.json';
                  link.click();
                  URL.revokeObjectURL(url);
                  showMessage('success', 'Data exported successfully');
                }
              }}
              disabled={!lifeExpectancyData}
            >
              Export Data
            </button>
          </div>
          
          <div>
            <p>Import updated life expectancy data:</p>
            <input
              type="file"
              accept=".json"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    try {
                      const data = JSON.parse(event.target?.result as string);
                      // Update parameters from imported data
                      if (data.standard_deviation !== undefined) {
                        setParameters(prev => ({
                          ...prev,
                          standard_deviation: data.standard_deviation,
                          standard_deviation_source: data.standard_deviation_source || prev.standard_deviation_source,
                          female_premium: data.female_premium || prev.female_premium,
                          female_premium_source: data.female_premium_source || prev.female_premium_source
                        }));
                      }
                      updateLifeExpectancyTable(data);
                    } catch (error) {
                      showMessage('error', 'Invalid JSON file');
                    }
                  };
                  reader.readAsText(file);
                }
              }}
              style={{ marginTop: '8px' }}
            />
          </div>
        </div>
      </div>

      {/* All Saved Scenarios */}
      <div style={adminSectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0 }}>All Saved Scenarios ({scenarios.length})</h3>
          <button
            style={adminButtonStyle}
            onClick={fetchAllScenarios}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px 8px', textAlign: 'left' }}>Calculator</th>
                <th style={{ padding: '12px 8px', textAlign: 'left' }}>User</th>
                <th style={{ padding: '12px 8px', textAlign: 'left' }}>Tier</th>
                <th style={{ padding: '12px 8px', textAlign: 'left' }}>Scenario Name</th>
                <th style={{ padding: '12px 8px', textAlign: 'left' }}>Created</th>
                <th style={{ padding: '12px 8px', textAlign: 'left' }}>Updated</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                    No scenarios found
                  </td>
                </tr>
              ) : (
                scenarios.map((scenario, index) => (
                  <tr key={scenario.id} style={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <td style={{ padding: '12px 8px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {scenario.calculator}
                      </span>
                    </td>
                    <td style={{ padding: '12px 8px' }}>{scenario.user_email}</td>
                    <td style={{ padding: '12px 8px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: scenario.user_tier === 'supporter' ? '#dcfce7' : '#f3f4f6',
                        color: scenario.user_tier === 'supporter' ? '#166534' : '#374151',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {scenario.user_tier}
                      </span>
                    </td>
                    <td style={{ padding: '12px 8px', fontWeight: '500' }}>{scenario.scenario_name}</td>
                    <td style={{ padding: '12px 8px', color: '#6b7280', fontSize: '13px' }}>
                      {new Date(scenario.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px 8px', color: '#6b7280', fontSize: '13px' }}>
                      {scenario.updated_at ? new Date(scenario.updated_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel; 